/**
 * Customer support ticket data hooks.
 *
 * These call the api-alpha `/v1/support/tickets/*` routes directly through the
 * shared `client` wrapper (the same approach as `useContactSupport.ts`) because
 * the endpoints are not in the generated OpenAPI SDK yet and regenerating
 * requires a running backend. Envelope handling mirrors the generated-SDK hooks.
 *
 * Live updates arrive over the shared WebSocket via `useChatRealtime`, which
 * invalidates the keys below on `chat.message.*` events carrying a `ticketId`.
 * TanStack Query's refetch-on-focus plus invalidate-on-mutate remain as
 * fallbacks when the socket is briefly down.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { client } from "@/services/api/client.gen";
import type {
  ApiEnvelope,
  SupportTicketListItem,
  SupportTicketDetail,
} from "@/types/support";

/**
 * Query-key builders. Exported so the WebSocket live-append consumer
 * (`useChatRealtime`) can invalidate exactly these keys without drifting from
 * the shapes used here.
 */
export const TICKETS_ROOT_KEY = ["support", "tickets"] as const;
export const ticketDetailKey = (ticketId: string) =>
  ["support", "ticket", ticketId] as const;

const DEFAULT_LIST_LIMIT = 50;
const DEFAULT_THREAD_LIMIT = 100;

/**
 * The caller's own support tickets, newest-first. `includeClosed` toggles
 * whether resolved tickets are returned.
 */
export function useSupportTickets(
  includeClosed: boolean,
): UseQueryResult<SupportTicketListItem[]> {
  return useQuery({
    queryKey: [...TICKETS_ROOT_KEY, { includeClosed }],
    queryFn: async () => {
      const result = await client.get({
        url: "/v1/support/tickets",
        query: {
          limit: DEFAULT_LIST_LIMIT,
          include_closed: includeClosed ? "true" : "false",
        },
        throwOnError: true,
      });
      const envelope = result.data as
        ApiEnvelope<SupportTicketListItem[]> | undefined;
      return envelope?.data ?? [];
    },
    staleTime: 30 * 1000,
  });
}

/**
 * One ticket plus its customer-facing conversation thread. Disabled until a
 * ticket id is provided.
 */
export function useSupportTicket(
  ticketId: string | null,
): UseQueryResult<SupportTicketDetail | null> {
  return useQuery({
    queryKey: ticketId
      ? ticketDetailKey(ticketId)
      : ["support", "ticket", null],
    enabled: Boolean(ticketId),
    queryFn: async () => {
      const result = await client.get({
        url: `/v1/support/tickets/${ticketId}`,
        query: { limit: DEFAULT_THREAD_LIMIT, offset: 0 },
        throwOnError: true,
      });
      const envelope = result.data as
        ApiEnvelope<SupportTicketDetail> | undefined;
      return envelope?.data ?? null;
    },
    staleTime: 15 * 1000,
  });
}

/** Post a reply. Replying to a closed ticket auto-reopens it server-side. */
export function useReplyToTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      const result = await client.post({
        url: `/v1/support/tickets/${ticketId}/reply`,
        body: { content },
        throwOnError: true,
      });
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketDetailKey(ticketId),
      });
      void queryClient.invalidateQueries({ queryKey: TICKETS_ROOT_KEY });
    },
  });
}

/** Close (resolve) a ticket. Idempotent server-side. */
export function useCloseTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await client.post({
        url: `/v1/support/tickets/${ticketId}/close`,
        throwOnError: true,
      });
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketDetailKey(ticketId),
      });
      void queryClient.invalidateQueries({ queryKey: TICKETS_ROOT_KEY });
    },
  });
}

/** Reopen a closed ticket. Idempotent server-side. */
export function useReopenTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await client.post({
        url: `/v1/support/tickets/${ticketId}/reopen`,
        throwOnError: true,
      });
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketDetailKey(ticketId),
      });
      void queryClient.invalidateQueries({ queryKey: TICKETS_ROOT_KEY });
    },
  });
}
